import React from 'react';
import {
	Box,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	TextField,
	Typography,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
	DEFAULT_RECURRENCE_FORM,
	MONTH_OPTIONS,
	MONTH_PATTERN_WEEKS,
	RECURRING_FREQUENCY_OPTIONS,
	RecurrenceFormState,
	WEEKDAY_OPTIONS,
} from 'module/toDoPage/utils/recurringTodoUtils';

interface FrequencyOption {
	id: number;
	label: string;
}

interface RecurrencePickerProps {
	value: RecurrenceFormState;
	onChange: (value: RecurrenceFormState) => void;
	frequencyOptions: FrequencyOption[];
	disabled?: boolean;
	errors?: Partial<Record<string, string>>;
}

const RecurrencePicker: React.FC<RecurrencePickerProps> = ({
	value,
	onChange,
	frequencyOptions,
	disabled = false,
	errors = {},
}) => {
	const resolvedFrequencies =
		frequencyOptions.length > 0
			? frequencyOptions
			: RECURRING_FREQUENCY_OPTIONS.map((label, index) => ({ id: index + 1, label }));

	const handleFrequencyChange = (label: string) => {
		const match = resolvedFrequencies.find(
			(option) => option.label.toLowerCase() === label.toLowerCase()
		);
		onChange({
			...value,
			frequencyLabel: label as RecurrenceFormState['frequencyLabel'],
			frequencyId: match?.id ?? value.frequencyId,
		});
	};

	const handleTimeChange = (time: Dayjs | null) => {
		onChange({
			...value,
			scheduledTime: time ? time.format('HH:mm:ss') : '',
		});
	};

	return (
		<Box
			sx={{
				border: '1px solid rgba(0,0,0,0.12)',
				borderRadius: '8px',
				p: 2,
				backgroundColor: '#fafafa',
			}}
		>
			<Typography variant="subtitle2" sx={{ mb: 2, color: '#3c5471', fontWeight: 600 }}>
				Recurrence Schedule
			</Typography>

			<Box display="flex" flexDirection="column" gap={2}>
				<FormControl fullWidth disabled={disabled}>
					<InputLabel shrink>Frequency</InputLabel>
					<Select
						value={value.frequencyLabel}
						label="Frequency"
						onChange={(e) => handleFrequencyChange(String(e.target.value))}
						displayEmpty
					>
						{resolvedFrequencies.map((option) => (
							<MenuItem key={option.id} value={option.label}>
								{option.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>

				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<TimePicker
						label="Time"
						value={value.scheduledTime ? dayjs(value.scheduledTime, 'HH:mm:ss') : null}
						onChange={handleTimeChange}
						disabled={disabled}
						slotProps={{
							textField: {
								fullWidth: true,
								required: true,
								error: !!errors.scheduledTime,
								helperText: errors.scheduledTime,
							},
						}}
					/>
				</LocalizationProvider>

				{value.frequencyLabel === 'Weekly' && (
					<FormControl fullWidth disabled={disabled} error={!!errors.dayOfWeek}>
						<InputLabel shrink>Day of Week</InputLabel>
						<Select
							value={value.dayOfWeek}
							label="Day of Week"
							onChange={(e) => onChange({ ...value, dayOfWeek: String(e.target.value) })}
						>
							{WEEKDAY_OPTIONS.map((day) => (
								<MenuItem key={day} value={day}>
									{day}
								</MenuItem>
							))}
						</Select>
						{errors.dayOfWeek && (
							<Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
								{errors.dayOfWeek}
							</Typography>
						)}
					</FormControl>
				)}

				{value.frequencyLabel === 'Monthly' && (
					<Box display="flex" flexDirection="column" gap={2}>
						<Typography variant="body2" color="text.secondary">
							Monthly schedule type
						</Typography>
						<RadioGroup
							row
							value={value.monthlyScheduleType}
							onChange={(e) =>
								onChange({
									...value,
									monthlyScheduleType: e.target.value as RecurrenceFormState['monthlyScheduleType'],
								})
							}
						>
							<FormControlLabel
								value="specific_dates"
								control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} disabled={disabled} />}
								label="Day of month (e.g. 15th)"
								disabled={disabled}
							/>
							<FormControlLabel
								value="recurring_days"
								control={<Radio sx={{ color: '#446388', '&.Mui-checked': { color: '#446388' } }} disabled={disabled} />}
								label="Day pattern (e.g. 2nd Thursday)"
								disabled={disabled}
							/>
						</RadioGroup>

						{value.monthlyScheduleType === 'specific_dates' ? (
							<TextField
								label="Day of Month"
								type="number"
								fullWidth
								disabled={disabled}
								value={value.dayOfMonth}
								onChange={(e) =>
									onChange({
										...value,
										dayOfMonth: Number(e.target.value) || DEFAULT_RECURRENCE_FORM.dayOfMonth,
									})
								}
								inputProps={{ min: 1, max: 31 }}
								error={!!errors.dayOfMonth}
								helperText={errors.dayOfMonth}
							/>
						) : (
							<Box display="flex" gap={2}>
								<FormControl fullWidth disabled={disabled} error={!!errors.monthPatternWeek}>
									<InputLabel shrink>Week of Month</InputLabel>
									<Select
										value={value.monthPatternWeek}
										label="Week of Month"
										onChange={(e) => onChange({ ...value, monthPatternWeek: String(e.target.value) })}
									>
										{MONTH_PATTERN_WEEKS.map((week) => (
											<MenuItem key={week} value={week}>
												{week.charAt(0).toUpperCase() + week.slice(1)}
											</MenuItem>
										))}
									</Select>
									{errors.monthPatternWeek && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
											{errors.monthPatternWeek}
										</Typography>
									)}
								</FormControl>
								<FormControl fullWidth disabled={disabled} error={!!errors.monthPatternDay}>
									<InputLabel shrink>Day of Week</InputLabel>
									<Select
										value={value.monthPatternDay}
										label="Day of Week"
										onChange={(e) => onChange({ ...value, monthPatternDay: String(e.target.value) })}
									>
										{WEEKDAY_OPTIONS.map((day) => (
											<MenuItem key={day} value={day}>
												{day}
											</MenuItem>
										))}
									</Select>
									{errors.monthPatternDay && (
										<Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
											{errors.monthPatternDay}
										</Typography>
									)}
								</FormControl>
							</Box>
						)}
					</Box>
				)}

				{value.frequencyLabel === 'Annually' && (
					<Box display="flex" gap={2}>
						<FormControl fullWidth disabled={disabled} error={!!errors.annualMonth}>
							<InputLabel shrink>Month</InputLabel>
							<Select
								value={value.annualMonth}
								label="Month"
								onChange={(e) => onChange({ ...value, annualMonth: Number(e.target.value) })}
							>
								{MONTH_OPTIONS.map((month) => (
									<MenuItem key={month.value} value={month.value}>
										{month.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							label="Day"
							type="number"
							fullWidth
							disabled={disabled}
							value={value.annualDay}
							onChange={(e) =>
								onChange({ ...value, annualDay: Number(e.target.value) || DEFAULT_RECURRENCE_FORM.annualDay })
							}
							inputProps={{ min: 1, max: 31 }}
							error={!!errors.annualDay}
							helperText={errors.annualDay}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default RecurrencePicker;
